import { Avatar, AvatarFallback } from "@bednbody/ui/components/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@bednbody/ui/components/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@bednbody/ui/components/sidebar";
import { useTheme } from "@bednbody/ui/lib/theme";
import { MAP_STYLE_CHOICES, useMapStyleChoice } from "@bednbody/ui/lib/map-style";
import { CaretUpDownIcon, MapTrifoldIcon, MoonIcon, SignOutIcon, SunIcon } from "@phosphor-icons/react";

interface NavUserProps {
  email: string | null;
  role: string;
  signOut: () => Promise<void>;
}

export function NavUser({ email, role, signOut }: NavUserProps) {
  const { isMobile } = useSidebar();
  const { theme, setTheme } = useTheme();
  // Leon, 2026-10-23 — the map setting every app has; here one item cycles it.
  const [mapStyle, setMapStyle] = useMapStyleChoice();
  const mapIndex = MAP_STYLE_CHOICES.findIndex((c) => c.value === mapStyle);
  const mapLabel = MAP_STYLE_CHOICES[mapIndex]?.label ?? "Auto";
  const nextMapStyle = MAP_STYLE_CHOICES[(mapIndex + 1) % MAP_STYLE_CHOICES.length]!.value;
  const initials = (email?.[0] ?? role[0] ?? "?").toUpperCase();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="size-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium capitalize">{role.replace("_", " ")}</span>
                  <span className="truncate text-xs">{email ?? "Signed in"}</span>
                </div>
                <CaretUpDownIcon weight="fill" className="ml-auto size-4" />
              </SidebarMenuButton>
            }
          />
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuItem onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
              {theme === "dark" ? (
                <SunIcon weight="fill" className="size-4" />
              ) : (
                <MoonIcon weight="fill" className="size-4" />
              )}
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </DropdownMenuItem>
            <DropdownMenuItem
              closeOnClick={false}
              onClick={() => setMapStyle(nextMapStyle)}
            >
              <MapTrifoldIcon weight="fill" className="size-4" />
              Map style: {mapLabel}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut()} variant="destructive">
              <SignOutIcon weight="bold" className="size-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
